import { isAxiosError } from "axios";

import api, {
  MutationRequestOptions,
  getMutationRequestConfig,
} from "../utils/axiosConfig";
import type { TokenResponse, UserResponse } from "../types/user";

interface CurrentUserRoleInfo {
  name: string;
  priority: number;
}

interface CurrentUserRBACResponse {
  roles: CurrentUserRoleInfo[];
  effective_permissions: string[];
  can_review_history?: boolean;
  can_export_history_dataset?: boolean;
  can_access_manual_recognition?: boolean;
}

interface CurrentUserLegacyPermissionResponse {
  role?: string;
  region_id?: number | null;
  permissions?: string[];
}

const SESSION_ENDPOINT_FALLBACK_STATUSES = new Set([404, 501]);

const withFallbackPermissions = (
  user: UserResponse,
  overrides?: Partial<
    Pick<
      UserResponse,
      | "role"
      | "region_id"
      | "permissions"
      | "can_review_history"
      | "can_export_history_dataset"
      | "can_access_manual_recognition"
    >
  >,
): UserResponse => ({
  ...user,
  role: overrides?.role ?? user.role,
  region_id: overrides?.region_id ?? user.region_id,
  permissions: overrides?.permissions ?? user.permissions ?? [],
  can_review_history:
    overrides?.can_review_history ?? user.can_review_history ?? false,
  can_export_history_dataset:
    overrides?.can_export_history_dataset ??
    user.can_export_history_dataset ??
    false,
  can_access_manual_recognition:
    overrides?.can_access_manual_recognition ??
    user.can_access_manual_recognition ??
    false,
});

const isInvalidCredentialsError = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false;
  }

  return (
    error.response?.status === 400 &&
    error.response?.data?.detail === "LOGIN_BAD_CREDENTIALS"
  );
};

const shouldFallbackToLegacySession = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  if (status == null) {
    return false;
  }

  return SESSION_ENDPOINT_FALLBACK_STATUSES.has(status) || status >= 500;
};

const getCurrentUserFromLegacyEndpoints = async (): Promise<UserResponse> => {
  const userResponse = await api.get<UserResponse>("/users/me");
  const user = userResponse.data;

  try {
    const rbacResponse = await api.get<CurrentUserRBACResponse>(
      "/rbac/users/me/roles",
    );
    const primaryRole = [...(rbacResponse.data.roles || [])].sort(
      (left, right) => right.priority - left.priority,
    )[0];

    return withFallbackPermissions(user, {
      role: primaryRole?.name ?? user.role,
      permissions: rbacResponse.data.effective_permissions ?? [],
      can_review_history: rbacResponse.data.can_review_history ?? false,
      can_export_history_dataset:
        rbacResponse.data.can_export_history_dataset ?? false,
      can_access_manual_recognition:
        rbacResponse.data.can_access_manual_recognition ?? false,
    });
  } catch (rbacError) {
    try {
      const permissionResponse =
        await api.get<CurrentUserLegacyPermissionResponse>("/roles/users/me");

      return withFallbackPermissions(user, {
        role: permissionResponse.data.role ?? user.role,
        region_id: permissionResponse.data.region_id ?? user.region_id,
        permissions: permissionResponse.data.permissions ?? [],
      });
    } catch (legacyPermissionError) {
      console.warn("权限信息加载失败，回退到基础用户信息", {
        rbacError,
        legacyPermissionError,
      });
      return withFallbackPermissions(user);
    }
  }
};

export const login = async (
  account: string,
  password: string,
): Promise<TokenResponse | null> => {
  try {
    const formData = new URLSearchParams();
    formData.append("username", account);
    formData.append("password", password);

    const response = await api.post("/auth/login", formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      ...getMutationRequestConfig({
        preventDuplicate: false, // mutation-policy: allow-concurrent login requests
      }),
    });

    const { access_token } = response.data;
    localStorage.setItem("accessToken", access_token);
    api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

    return response.data;
  } catch (error) {
    if (isInvalidCredentialsError(error)) {
      return null;
    }

    throw error;
  }
};

export const refreshAccessToken = async (): Promise<TokenResponse> => {
  const response = await api.post<TokenResponse>(
    "/auth/refresh",
    undefined,
    getMutationRequestConfig({
      preventDuplicate: false, // mutation-policy: allow-concurrent token refresh requests
    }),
  );
  const { access_token } = response.data;
  localStorage.setItem("accessToken", access_token);
  api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
  return response.data;
};

export const getCurrentUser = async (): Promise<UserResponse | null> => {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    return null;
  }

  try {
    try {
      const sessionResponse = await api.get<UserResponse>("/auth/session");
      return withFallbackPermissions(sessionResponse.data);
    } catch (sessionError) {
      if (!shouldFallbackToLegacySession(sessionError)) {
        throw sessionError;
      }

      return await getCurrentUserFromLegacyEndpoints();
    }
  } catch (error) {
    console.error("获取用户信息失败", error);
    if (isAxiosError(error) && error.response?.status === 401) {
      const currentToken = localStorage.getItem("accessToken");

      if (!currentToken || currentToken === token) {
        logout();
        return null;
      }
    }
    throw error;
  }
};

export const updatePassword = async (
  currentPassword: string,
  newPassword: string,
  options?: MutationRequestOptions,
): Promise<void> => {
  await api.patch(
    "/users/me/password",
    {
      current_password: currentPassword,
      new_password: newPassword,
    },
    getMutationRequestConfig(options),
  );
};

export const logout = (): void => {
  localStorage.removeItem("accessToken");
  delete api.defaults.headers.common["Authorization"];
};

export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem("accessToken");
};
